/**
 * AppointmentsPage — Módulo Agenda
 * FASE 1: Tabs Dashboard, Calendario, Pendientes, Configuración (0257)
 * Acceso: solo ADMIN o usuarios vinculados como Professional en Agenda > Equipo.
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import GuideOpenButton from "../../components/GuideOpenButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { useAppointmentFilters } from "./hooks/useAppointmentFilters";
import { useAppointments } from "./hooks/useAppointments";
import AppointmentsCalendar from "./AppointmentsCalendar";
import AppointmentsDashboard from "./AppointmentsDashboard";
import AppointmentsPendientes from "./AppointmentsPendientes";
import AppointmentsHistorial from "./AppointmentsHistorial";
import AppointmentsConfig from "./AppointmentsConfig";
import AppointmentModal from "../../components/AppointmentModal";
import Typography from "@material-ui/core/Typography";
import { useStyles } from "./styles";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const FILTER_STATUS_DEF = [
  { value: "", tKey: "all" },
  { value: "pending", tKey: "pending" },
  { value: "confirmed", tKey: "confirmed" },
  { value: "rescheduled", tKey: "rescheduled" },
  { value: "in_progress", tKey: "in_progress" },
  { value: "completed", tKey: "completed" },
  { value: "no_show", tKey: "no_show" },
  { value: "cancelled", tKey: "cancelled" },
];

export default function AppointmentsPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [canAccessAgenda, setCanAccessAgenda] = useState(null);
  const [canAccessConfig, setCanAccessConfig] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const filters = useAppointmentFilters();
  const params = useMemo(() => filters.params(), [filters.dateFrom, filters.dateTo, filters.professionalId, filters.status]);
  const { appointments, loading, fetchAppointments } = useAppointments(params);

  const statusOptions = useMemo(
    () =>
      FILTER_STATUS_DEF.map((o) => ({
        value: o.value,
        label: i18n.t(`appointmentsModule.status.${o.tKey}`),
      })),
    [i18n.language],
  );

  const tabLabels = useMemo(
    () => [
      i18n.t("appointmentsModule.page.tabs.dashboard"),
      i18n.t("appointmentsModule.page.tabs.calendar"),
      i18n.t("appointmentsModule.page.tabs.pending"),
      i18n.t("appointmentsModule.page.tabs.history"),
      i18n.t("appointmentsModule.page.tabs.settings"),
    ],
    [i18n.language],
  );

  const pendingCount = appointments.filter((a) => a.status === "pending").length;

  const handleEdit = (a) => { setSelectedAppointment(a); setModalOpen(true); };
  const handleCloseModal = () => { setSelectedAppointment(null); setModalOpen(false); fetchAppointments(); };
  const handleUpdateStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}/status`, { status });
      fetchAppointments();
    } catch (e) { toastError(e); }
  };

  const pendientes = useMemo(() => appointments.filter((a) => a.status === "pending"), [appointments]);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/users/can-access-agenda");
        if (!cancelled && isMountedRef.current) {
          setCanAccessAgenda(data?.canAccessAgenda === true);
          setCanAccessConfig(data?.canAccessConfig === true);
        }
      } catch (e) {
        if (!cancelled && isMountedRef.current) {
          setCanAccessAgenda(false);
          setCanAccessConfig(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!canAccessConfig && tabIndex >= 4) setTabIndex(3);
  }, [canAccessConfig, tabIndex]);

  useEffect(() => {
    if (canAccessAgenda !== true) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/onboarding/config");
        if (!cancelled && isMountedRef.current) {
          setProfessionals(data?.professionals || []);
        }
      } catch (e) {
        if (!cancelled && isMountedRef.current) setProfessionals([]);
      }
    })();
    return () => { cancelled = true; };
  }, [canAccessAgenda]);

  // Tab desde navegación (ej. Kanban → icono reloj → Agenda Calendario)
  useEffect(() => {
    const fromState = location.state?.tabIndex;
    if (typeof fromState === "number" && fromState >= 0 && fromState <= 4) {
      setTabIndex(fromState);
    }
  }, [location.state?.tabIndex]);

  const handleConfirm = async (a) => {
    if (!a?.id || updatingId) return;
    setUpdatingId(a.id);
    try {
      await api.patch(`/appointments/${a.id}/status`, { status: "confirmed" });
      fetchAppointments();
    } catch (e) { toastError(e); }
    finally { setUpdatingId(null); }
  };

  const handleCancel = async (a) => {
    if (!a?.id || updatingId) return;
    setUpdatingId(a.id);
    try {
      await api.patch(`/appointments/${a.id}/status`, { status: "cancelled" });
      fetchAppointments();
    } catch (e) { toastError(e); }
    finally { setUpdatingId(null); }
  };

  if (canAccessAgenda === false) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>{i18n.t("appointmentsModule.page.moduleTitle")}</Title>
        </MainHeader>
        <div style={{ padding: 24, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            {i18n.t("appointmentsModule.page.accessDeniedTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {i18n.t("appointmentsModule.page.accessDeniedP1")}
            <br />
            {i18n.t("appointmentsModule.page.accessDeniedP2")}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => history.push("/")}>
            {i18n.t("appointmentsModule.page.goHome")}
          </Button>
        </div>
      </MainContainer>
    );
  }

  if (canAccessAgenda === null) {
    return (
      <MainContainer>
        <MainHeader><Title>{i18n.t("appointmentsModule.page.moduleTitle")}</Title></MainHeader>
        <div style={{ padding: 24, textAlign: "center" }}><Typography>{i18n.t("appointmentsModule.page.checkingAccess")}</Typography></div>
      </MainContainer>
    );
  }

  const TABS = canAccessConfig ? tabLabels : tabLabels.slice(0, 4);

  const renderContent = () => {
    if (tabIndex === 0) return <AppointmentsDashboard appointments={appointments} onSelectAppointment={handleEdit} />;
    if (tabIndex === 1) return <AppointmentsCalendar appointments={appointments} onSelectEvent={(e) => handleEdit(e?.resource)} />;
    if (tabIndex === 2) {
      return (
        <AppointmentsPendientes
          appointments={pendientes}
          loading={loading}
          onConfirm={handleConfirm}
          onReschedule={handleEdit}
          onCancel={handleCancel}
          updatingId={updatingId}
        />
      );
    }
    if (tabIndex === 3) {
      return (
        <AppointmentsHistorial
          appointments={appointments}
          loading={loading}
          onEdit={handleEdit}
        />
      );
    }
    return <AppointmentsConfig />;
  };

  return (
    <MainContainer>
      <div className={classes.pageRoot}>
        <MainHeader>
          <Title>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {i18n.t("appointmentsModule.page.moduleTitle")}
              <GuideOpenButton id="agenda" title={i18n.t("appointmentsModule.page.moduleTitle")} to="/appointments" startId="calendar" />
            </span>
          </Title>
          {pendingCount > 0 && (
            <Typography variant="body2" color="secondary" style={{ marginLeft: 8 }}>
              {i18n.t("appointmentsModule.page.pendingLine", { count: pendingCount })}
            </Typography>
          )}
          <MainHeaderButtonsWrapper>
            {tabIndex !== 4 && (
              <>
                <TextField type="date" size="small" value={filters.dateFrom} onChange={(e) => filters.setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} style={{ marginRight: 8 }} />
                <TextField type="date" size="small" value={filters.dateTo} onChange={(e) => filters.setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} style={{ marginRight: 8 }} />
                {professionals.length > 0 && (
                  <TextField select size="small" value={String(filters.professionalId || "")} onChange={(e) => filters.setProfessionalId(e.target.value)} style={{ minWidth: 160, marginRight: 8 }}>
                    <MenuItem value="">{i18n.t("appointmentsModule.page.allProfessionals")}</MenuItem>
                    {professionals.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField select size="small" value={filters.status} onChange={(e) => filters.setStatus(e.target.value)} style={{ minWidth: 120, marginRight: 8 }}>
                  {statusOptions.map((o) => <MenuItem key={o.value || "all"} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </>
            )}
            <Button variant="contained" color="primary" onClick={() => { setSelectedAppointment(null); setModalOpen(true); }}>
              {i18n.t("appointmentsModule.page.newAppointment")}
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} className={classes.tabs}>
          {TABS.map((label, i) => (
            <Tab
              key={label}
              label={i === 2 && pendingCount > 0 ? `${label} (${pendingCount})` : label}
              className={classes.tab}
            />
          ))}
        </Tabs>
        <div className={classes.contentScroll}>
          {renderContent()}
        </div>
        {modalOpen && (
          <AppointmentModal
            open={modalOpen}
            onClose={handleCloseModal}
            appointment={selectedAppointment}
            onSaved={handleCloseModal}
            onStatusChange={handleUpdateStatus}
          />
        )}
      </div>
    </MainContainer>
  );
}
